import { PrismaEnums } from '@airreading/schema/browser';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { classTimeTypeSchema } from './class-time-shared.js';
import { studentGroupTypeSchema } from './learning-path.js';
import { filteringSchemaFrom, pagingSchema, sortingSchemaFrom, sortOrderSchemaFrom } from './modifier.js';
import { paginatedResponse } from './util-schema-types.js';
import { userLikeSchema } from './user.js';

export const studentGroupReassignmentReasonSchema = z.enum([
  'tns_spp',
  'exceeded_planned_absences_spp',
  'exceeded_short_notice_absences_spp',
  'prohibit_act_policy_violation',
  'tq_issue',
  'student_leveling_issue',
  'contract_termination_or_non_renewal',
  'unresponsive_to_zoom_link',
  'school_complaint',
  'teacher_request',
]);
export type StudentGroupReassignmentReason = z.infer<typeof studentGroupReassignmentReasonSchema>;
export const StudentGroupReassignmentReason = studentGroupReassignmentReasonSchema.enum;

export const studentGroupReassignmentReasonValues = studentGroupReassignmentReasonSchema.options;

export const StudentGroupReassignmentReasonLabelMap: Record<StudentGroupReassignmentReason, string> = {
  [StudentGroupReassignmentReason.tns_spp]: 'TNS (SPP)',
  [StudentGroupReassignmentReason.exceeded_planned_absences_spp]: 'Exceeded total allowance (SPP)',
  [StudentGroupReassignmentReason.exceeded_short_notice_absences_spp]: 'Exceeded short-notice allowance (SPP)',
  [StudentGroupReassignmentReason.prohibit_act_policy_violation]: 'Prohibit act policy violation',
  [StudentGroupReassignmentReason.tq_issue]: 'TQ issue',
  [StudentGroupReassignmentReason.student_leveling_issue]: 'Student leveling issue',
  [StudentGroupReassignmentReason.contract_termination_or_non_renewal]: 'Contract termination or non-renewal',
  [StudentGroupReassignmentReason.unresponsive_to_zoom_link]: 'Unresponsive to Zoom link',
  [StudentGroupReassignmentReason.school_complaint]: 'School complaint',
  [StudentGroupReassignmentReason.teacher_request]: 'Teacher request',
};

export const studentGroupReassignmentReasonOptions = studentGroupReassignmentReasonValues.map((value) => ({
  label: StudentGroupReassignmentReasonLabelMap[value],
  value,
}));

/** Attendance-threshold reassignment reasons (cannot authorize a final-14-day override by themselves). */
export const attendanceReassignmentReasons = [
  StudentGroupReassignmentReason.tns_spp,
  StudentGroupReassignmentReason.exceeded_planned_absences_spp,
  StudentGroupReassignmentReason.exceeded_short_notice_absences_spp,
] as const satisfies readonly StudentGroupReassignmentReason[];

/**
 * Required when changing a group's coach during the final-14-day protected period (flag on).
 * Free-text non-attendance reason only — attendance thresholds / no-shows / late / early exit
 * cannot satisfy this requirement.
 */
export const protectedPeriodOverrideSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type ProtectedPeriodOverride = z.infer<typeof protectedPeriodOverrideSchema>;

export function formatReassignmentReason(request: {
  reasons: StudentGroupReassignmentReason[];
  teacherReason: string | null;
}): string {
  const teacherReason = request.teacherReason?.trim();
  if (teacherReason) {
    return teacherReason;
  }
  return request.reasons.map((reason) => StudentGroupReassignmentReasonLabelMap[reason]).join(', ') || '-';
}

const studentGroupReassignmentRequestBaseSchema = z.object({
  studentGroupId: z.string().uuid(),
  effectivePlainDate: z.instanceof(Temporal.PlainDate),
  requesterId: z.string().uuid(),
  reasons: z.array(studentGroupReassignmentReasonSchema),
  preferredCoachId: z.preprocess(
    (val) => (typeof val === 'string' && val.trim() === '' ? null : val),
    z.string().uuid().nullable().optional(),
  ),
  specialRequirements: z.preprocess(
    (val) => (typeof val === 'string' && val.trim() === '' ? null : val),
    z.string().max(150).nullable().optional(),
  ),
  countAsReassignedFromTestTeacher: z.boolean(),
  teacherReason: z.preprocess(
    (val) => (typeof val === 'string' && val.trim() === '' ? null : val),
    z.string().max(150).nullable().optional(),
  ),
});

const requireReasonOrTeacherReason = (data: { reasons: StudentGroupReassignmentReason[]; teacherReason?: unknown }) =>
  data.reasons.length > 0 || !!data.teacherReason;

const requireReasonOrTeacherReasonError = {
  message: 'At least one reassignment reason or a teacher reason is required',
  path: ['reasons'],
};

export const createStudentGroupReassignmentRequestSchema = studentGroupReassignmentRequestBaseSchema.refine(
  requireReasonOrTeacherReason,
  requireReasonOrTeacherReasonError,
);
export type CreateStudentGroupReassignmentRequest = z.infer<typeof createStudentGroupReassignmentRequestSchema>;

export const createStudentGroupReassignmentRequestResponseSchema = z.object({
  id: z.string(),
});
export type CreateStudentGroupReassignmentRequestResponse = z.infer<
  typeof createStudentGroupReassignmentRequestResponseSchema
>;

export const updateStudentGroupReassignmentRequestSchema = studentGroupReassignmentRequestBaseSchema
  .omit({ studentGroupId: true })
  .refine(requireReasonOrTeacherReason, requireReasonOrTeacherReasonError);
export type UpdateStudentGroupReassignmentRequest = z.infer<typeof updateStudentGroupReassignmentRequestSchema>;
export const updateStudentGroupReassignmentRequestResponseSchema = z.object({
  id: z.string(),
});
export type UpdateStudentGroupReassignmentRequestResponse = z.infer<
  typeof updateStudentGroupReassignmentRequestResponseSchema
>;

export const studentGroupReassignmentRequestStatusSchema = z.nativeEnum(
  PrismaEnums.StudentGroupReassignmentRequestStatus,
);
export type StudentGroupReassignmentRequestStatus = z.infer<typeof studentGroupReassignmentRequestStatusSchema>;
export const StudentGroupReassignmentRequestStatus = studentGroupReassignmentRequestStatusSchema.enum;

export const StudentGroupReassignmentRequestStatusLabelMap: Record<StudentGroupReassignmentRequestStatus, string> = {
  [StudentGroupReassignmentRequestStatus.requested]: 'Requested',
  [StudentGroupReassignmentRequestStatus.processing]: 'Processing',
  [StudentGroupReassignmentRequestStatus.resolved]: 'Resolved',
};

const pendingReassignmentSchoolSchema = z.object({
  id: z.string(),
  name: z.string(),
  org: z.object({
    id: z.string(),
    name: z.string(),
  }),
});

/** Why a preferred new teacher was not chosen as the system suggested teacher. */
export const preferredCoachIneligibleReasonSchema = z.enum([
  'unavailable',
  'class_eligibility',
  'background_check',
  'tech_check',
]);
export type PreferredCoachIneligibleReason = z.infer<typeof preferredCoachIneligibleReasonSchema>;
export const PreferredCoachIneligibleReason = preferredCoachIneligibleReasonSchema.enum;

export const PreferredCoachIneligibleReasonLabelMap: Record<PreferredCoachIneligibleReason, string> = {
  [PreferredCoachIneligibleReason.unavailable]: 'Preferred new teacher is unavailable',
  [PreferredCoachIneligibleReason.class_eligibility]:
    'Preferred new teacher does not fulfill class eligibility criteria',
  [PreferredCoachIneligibleReason.background_check]: 'Preferred new teacher does not fulfill background check criteria',
  [PreferredCoachIneligibleReason.tech_check]: 'Preferred new teacher does not fulfill tech check criteria',
};

export const pendingStudentGroupReassignmentRequestSchema = z.object({
  id: z.string(),
  studentGroupId: z.string(),
  displayStatus: studentGroupReassignmentRequestStatusSchema,
  school: pendingReassignmentSchoolSchema,
  classTimeType: classTimeTypeSchema,
  originalCoach: userLikeSchema.nullable(),
  preferredCoach: userLikeSchema.nullable(),
  systemSuggestedCoach: userLikeSchema.nullable(),
  reassignedCoach: userLikeSchema.nullable(),
  requestPlainDate: z.instanceof(Temporal.PlainDate),
  effectivePlainDate: z.instanceof(Temporal.PlainDate),
  requester: userLikeSchema,
  reasons: z.array(studentGroupReassignmentReasonSchema),
  teacherReason: z.string().nullable(),
  specialRequirements: z.string().nullable(),
  countAsReassignedFromTestTeacher: z.boolean(),
  /** Count of reassignment requests for the same student group created before this one. */
  priorReassignmentRequestCount: z.number().int().nonnegative(),
  /**
   * Populated when suggestions are refreshed for the reassign modal; null on list endpoints.
   * Set when a preferred coach exists but was not chosen as system suggested.
   */
  preferredCoachIneligibleReason: preferredCoachIneligibleReasonSchema.nullable(),
});
export type PendingStudentGroupReassignmentRequest = z.infer<typeof pendingStudentGroupReassignmentRequestSchema>;

export const getPendingStudentGroupReassignmentRequestsResultsSchema = paginatedResponse.extend({
  reassignmentRequests: z.array(pendingStudentGroupReassignmentRequestSchema),
});
export type GetPendingStudentGroupReassignmentRequestsResults = z.infer<
  typeof getPendingStudentGroupReassignmentRequestsResultsSchema
>;

export const getPendingStudentGroupReassignmentRequestsFilterSchema = z.object({
  query: z.string().optional(),
  schools: z.array(z.string()).optional(),
  displayStatuses: z.array(studentGroupReassignmentRequestStatusSchema).optional(),
  types: z.array(studentGroupTypeSchema).optional(),
});
export type GetPendingStudentGroupReassignmentRequestsFilter = z.infer<
  typeof getPendingStudentGroupReassignmentRequestsFilterSchema
>;

export const getPendingStudentGroupReassignmentRequestsSortableColumnSchema = z.enum([
  'effectiveDate',
  'requestDate',
  'originalCoach',
  'reassignmentStatus',
]);
export type GetPendingStudentGroupReassignmentRequestsSortableColumn = z.infer<
  typeof getPendingStudentGroupReassignmentRequestsSortableColumnSchema
>;

export const getPendingStudentGroupReassignmentRequestsSortOrderSchema = sortOrderSchemaFrom(
  getPendingStudentGroupReassignmentRequestsSortableColumnSchema,
);
export type GetPendingStudentGroupReassignmentRequestsSortOrder = z.infer<
  typeof getPendingStudentGroupReassignmentRequestsSortOrderSchema
>;

export const getPendingStudentGroupReassignmentRequestsModifierSchema = pagingSchema
  .merge(filteringSchemaFrom(getPendingStudentGroupReassignmentRequestsFilterSchema))
  .merge(sortingSchemaFrom(getPendingStudentGroupReassignmentRequestsSortableColumnSchema));
export type GetPendingStudentGroupReassignmentRequestsModifier = z.infer<
  typeof getPendingStudentGroupReassignmentRequestsModifierSchema
>;

export const bulkUpdateStudentGroupReassignmentRequestsSchema = z.object({
  reassignmentRequestIds: z.array(z.string().uuid()).min(1),
  specialRequirements: z.preprocess(
    (val) => (typeof val === 'string' && val.trim() === '' ? null : val),
    z.string().max(150).nullable().optional(),
  ),
  reassignmentNotes: z.preprocess(
    (val) => (typeof val === 'string' && val.trim() === '' ? null : val),
    z.string().max(500).nullable().optional(),
  ),
});
export type BulkUpdateStudentGroupReassignmentRequests = z.infer<
  typeof bulkUpdateStudentGroupReassignmentRequestsSchema
>;

export const bulkUpdateStudentGroupReassignmentRequestsResultsSchema = z.object({
  success: z.number().int(),
});
export type BulkUpdateStudentGroupReassignmentRequestsResults = z.infer<
  typeof bulkUpdateStudentGroupReassignmentRequestsResultsSchema
>;

export const refreshSystemSuggestedCoachesSchema = z.object({
  reassignmentRequestIds: z.array(z.string().uuid()).min(1),
});
export type RefreshSystemSuggestedCoaches = z.infer<typeof refreshSystemSuggestedCoachesSchema>;

export const refreshSystemSuggestedCoachesResultsSchema = z.object({
  reassignmentRequests: z.array(pendingStudentGroupReassignmentRequestSchema),
});
export type RefreshSystemSuggestedCoachesResults = z.infer<typeof refreshSystemSuggestedCoachesResultsSchema>;

export const studentGroupReassignmentRecordSchema = z.object({
  id: z.string(),
  displayStatus: studentGroupReassignmentRequestStatusSchema,
  originalCoach: userLikeSchema.nullable(),
  preferredCoach: userLikeSchema.nullable(),
  systemSuggestedCoach: userLikeSchema.nullable(),
  reassignedCoach: userLikeSchema.nullable(),
  requestPlainDate: z.instanceof(Temporal.PlainDate),
  effectivePlainDate: z.instanceof(Temporal.PlainDate),
  requester: userLikeSchema,
  reasons: z.array(studentGroupReassignmentReasonSchema),
  reassignmentNotes: z.string().nullable(),
  teacherReason: z.string().nullable(),
});
export type StudentGroupReassignmentRecord = z.infer<typeof studentGroupReassignmentRecordSchema>;

export const getStudentGroupReassignmentRequestsResultsSchema = z.object({
  reassignmentRequests: z.array(studentGroupReassignmentRecordSchema),
});
export type GetStudentGroupReassignmentRequestsResults = z.infer<
  typeof getStudentGroupReassignmentRequestsResultsSchema
>;

export const bulkUpdateStudentGroupReassignmentRequestStatusOptionsSchema = z.object({
  reassignmentRequestIds: z.array(z.string().uuid()).min(1),
  status: studentGroupReassignmentRequestStatusSchema,
});
export type BulkUpdateStudentGroupReassignmentRequestStatusOptions = z.infer<
  typeof bulkUpdateStudentGroupReassignmentRequestStatusOptionsSchema
>;

export const bulkUpdateStudentGroupReassignmentRequestStatusResultsSchema = z.object({
  success: z.number().int(),
});
export type BulkUpdateStudentGroupReassignmentRequestStatusResults = z.infer<
  typeof bulkUpdateStudentGroupReassignmentRequestStatusResultsSchema
>;

/** Preview data for the pause → reassignment confirmation modal on teacher details. */
export const coachPauseReassignmentPreviewSchema = z.object({
  activeStudentGroupCount: z.number().int().nonnegative(),
  /** Last completed teaching session plain date in US/Central; null when none. */
  lastCompletedSessionPlainDate: z.instanceof(Temporal.PlainDate).nullable(),
});
export type CoachPauseReassignmentPreview = z.infer<typeof coachPauseReassignmentPreviewSchema>;

export const createCoachPauseReassignmentRequestsSchema = z.object({
  coachId: z.string().uuid(),
  effectivePlainDate: z.instanceof(Temporal.PlainDate),
  requesterId: z.string().uuid(),
  reasons: z.array(studentGroupReassignmentReasonSchema).min(1),
  preferredCoachId: z.preprocess(
    (val) => (typeof val === 'string' && val.trim() === '' ? null : val),
    z.string().uuid().nullable().optional(),
  ),
  specialRequirements: z.preprocess(
    (val) => (typeof val === 'string' && val.trim() === '' ? null : val),
    z.string().max(150).nullable().optional(),
  ),
});
export type CreateCoachPauseReassignmentRequests = z.infer<typeof createCoachPauseReassignmentRequestsSchema>;

export const createCoachPauseReassignmentRequestsResultsSchema = z.object({
  createdCount: z.number().int().nonnegative(),
  skippedAlreadyPendingCount: z.number().int().nonnegative(),
  skippedEffectiveDateAfterEndCount: z.number().int().nonnegative(),
});
export type CreateCoachPauseReassignmentRequestsResults = z.infer<
  typeof createCoachPauseReassignmentRequestsResultsSchema
>;
